// src/workspace/WorkspaceShell.jsx
//
// The analyst workspace's own chrome. Deliberately thin — DESIGN_analyst_workspace §5.
//
// ── WHY A SECOND SHELL AND NOT A FLAG ON THE FIRST ───────────────────────────
//
// AppShell is not just a header. It runs useOperatingAoiInit,
// useRestrictedCaseSelection, useRestrictedAutoEnableImagery,
// useRestrictedPadDrivenImagery, useRestrictedInitialLayers and
// useImpersonationFromUrl, and it loads the project list. Those hooks are what
// fetch /api/v1/aois, /projects/available, /padrekko/qc/*, /jobs and the layer
// sets — the eleven calls the access-boundary work catalogued as denied for a
// restricted account.
//
// A `if (isWorkspace)` branch inside AppShell would still mount every one of
// them, because hooks run before any conditional return. The analyst would keep
// making platform calls and keep collecting 403s, and the workspace would be a
// stripped platform rather than its own surface — which §1 of the design says
// it is not.
//
// So the shell is chosen STRUCTURALLY, by which layout route matched. See
// router.jsx: rootRoute renders a bare <Outlet/>, and two pathless layout
// routes sit under it — _platform renders AppShell, _workspace renders this.
// Nothing in either shell knows the other exists.
//
// ── WHAT IS DELIBERATELY ABSENT ──────────────────────────────────────────────
//
// No org switcher: an analyst has one org. No project selector: adjudication is
// not project-scoped. No mode strip: there is one tool. No notifications until
// something needs to notify. Each of those is a platform affordance that would
// also drag its data fetch behind it.
//
// ── HEIGHT IS A FEATURE ──────────────────────────────────────────────────────
//
// h-11 (44px). The review surface's map pane has a 480px floor, and on a 900px
// laptop viewport every pixel of chrome comes out of it. 44px leaves 856px,
// which clears the floor with the candidate column and the command bar intact.
// Anything added here has to justify itself against that number.
import { Outlet, Link, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { HelpCircle, LogOut } from "lucide-react";
import { useAuthStore } from "../stores/useAuthStore";
import { toolForPath } from "./tools";

export default function WorkspaceShell() {
  // The one fetch this shell makes. /auth/me is on the restricted allowlist and
  // supplies the display name; nothing else here needs the network.
  const loadProfile = useAuthStore((s) => s.loadProfile);
  const authToken = useAuthStore((s) => s.token);
  const profile = useAuthStore((s) => s.profile);

  useEffect(() => {
    if (!authToken) return;
    loadProfile();
  }, [authToken, loadProfile]);

  const path = useRouterState({ select: (s) => s.location.pathname });

  // WHICH TOOL, FROM THE ROUTE. The header used to read "WellFinder Review" on
  // every workspace page -- on the Feature Adjudicator, where it named the wrong
  // tool, and on the landing, where no tool is open at all.
  //
  // NULL IS A REAL ANSWER, not a miss. At /workspace the reviewer is choosing a
  // tool, so there is nothing to name and the header says nothing.
  const tool = toolForPath(path);
  const onReference = path.startsWith("/workspace/reference");

  const who = profile?.display_name || profile?.email || "";

  return (
    <div className="flex h-screen min-h-0 flex-col bg-bg text-fg-primary">
      <header className="flex h-11 shrink-0 items-center gap-4 border-b border-bg-border px-4">
        {/* THE TOOL YOU ARE IN, or nothing.
            
            On the landing this is deliberately empty: the cards ARE the
            navigation, and a product name above them would name one tool while
            the reviewer is choosing between two.
            
            It links back to /workspace, so the name doubles as the way out of a
            tool -- which is why there is no separate back affordance. */}
        {tool ? (
          <Link to="/workspace" className="text-sm font-medium tracking-tight hover:text-white">
            {tool.label}
          </Link>
        ) : onReference ? (
          <Link to="/workspace" className="text-sm font-medium tracking-tight hover:text-white">
            Reference
          </Link>
        ) : (
          <span className="text-sm font-medium tracking-tight text-fg-secondary">Workspace</span>
        )}

        {/* NO TOOL STRIP. It was one link to WellFinder's queue, written when
            there was one tool -- its own comment said "One tool today, so one
            link" and pointed at the design's §7, where this becomes cards on the
            landing. That has happened, so the strip is removed rather than grown
            into a mode switcher naming every tool twice. */}

        <div className="flex-1" />

        <Link
          to="/workspace/reference"
          title="Keys and reference"
          className="text-fg-secondary transition-colors hover:text-fg-primary"
        >
          <HelpCircle size={15} />
        </Link>

        <div className="flex items-center gap-2 text-xs text-fg-secondary">
          <span className="max-w-[16rem] truncate" title={who}>{who}</span>
          <button
            type="button"
            onClick={() => window.__keycloak?.logout({ redirectUri: window.location.origin })}
            title="Sign out"
            className="transition-colors hover:text-fg-primary"
          >
            <LogOut size={14} />
          </button>
        </div>
      </header>

      {/* min-h-0 so the review surface's internal flex column can shrink rather
          than overflow the viewport — the same reason its own panes carry it. */}
      <main className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}
